import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { Textarea } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import { type ReactElement, useState } from "react";

import {
    type SourceOfFundsPayload,
    submitSourceOfFunds
} from "@/services/clientOnboarding";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";
import { useReportOnboardingDraft } from "../onboardingDraftContext";
import { useFundsNarrativeFormState } from "../onboardingFormStateContext";
import { FormCard } from "../panel/FormCard";
import { FormPrimaryAction } from "../panel/FormPrimaryAction";
import type { NextStepTarget } from "../types";

const NARRATIVE_HEADER_SOURCE = "Source of funds:";
const NARRATIVE_HEADER_JURISDICTION = "Jurisdiction of operations:";

/**
 * Funds & Operations — slot 5.1. Two required narratives:
 *   1. where the treasury funds came from
 *   2. where the protocol materially operates
 *
 * Pure declaration is sufficient at submission. Supporting documents
 * live in slot 5.2 and are optional unless extended due diligence
 * triggers a request.
 */
export function FundsNarrativeForm({
    slotTitle,
    onSubmit,
    nextStep,
    onContinue
}: {
    slotTitle: string;
    onSubmit: (payload: unknown) => void;
    /** When non-null and the form is in a saved-clean state, the
     *  bottom button flips from "Saved" to "Continue to {title} →"
     *  and clicking it advances the hub to that subsection. */
    nextStep?: NextStepTarget | null;
    onContinue?: () => void;
}): ReactElement {
    const context = useOnboardingApplicationContextOrNull();
    const persistedStep = context?.stepsByName.source_of_funds;
    // Narrative field values + lastSubmitted snapshot live in the
    // hub-scoped form-state context so edits survive navigation. Server
    // hydration (incl. splitting the BE's combined sourceDescription) is
    // handled once inside OnboardingFormStateProvider.
    const [state, setState] = useFundsNarrativeFormState();
    const { sourceOfFunds, jurisdiction, lastSubmitted } = state;
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const submittable =
        sourceOfFunds.trim().length > 0 && jurisdiction.trim().length > 0;

    const isDirty =
        lastSubmitted !== null &&
        (sourceOfFunds.trim() !== lastSubmitted.sourceOfFunds ||
            jurisdiction.trim() !== lastSubmitted.jurisdiction);
    const hasPartialInput =
        sourceOfFunds.trim().length > 0 || jurisdiction.trim().length > 0;
    const hasDraft = isDirty || (lastSubmitted === null && hasPartialInput);
    useReportOnboardingDraft("source-of-funds", hasDraft);

    const handleSave = async (): Promise<void> => {
        if (!submittable) return;
        const description =
            `${NARRATIVE_HEADER_SOURCE}\n${sourceOfFunds.trim()}\n\n` +
            `${NARRATIVE_HEADER_JURISDICTION}\n${jurisdiction.trim()}`;
        const existing = persistedStep?.payload as
            | Partial<SourceOfFundsPayload>
            | undefined;
        const payload: SourceOfFundsPayload = {
            sourceCategory: "narrative",
            sourceDescription: description,
            supportingDocumentUuid: existing?.supportingDocumentUuid ?? null
        };

        const snapshot = {
            sourceOfFunds: sourceOfFunds.trim(),
            jurisdiction: jurisdiction.trim()
        };
        if (!context) {
            onSubmit(payload);
            setState((prev) => ({ ...prev, lastSubmitted: snapshot }));
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        try {
            const result = await submitSourceOfFunds(
                context.application.uuid,
                payload
            );
            context.applyStepUpdate(result);
            onSubmit(payload);
            setState((prev) => ({ ...prev, lastSubmitted: snapshot }));
        } catch (err) {
            setSubmitError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Source of funds was not saved. Complete both narratives and try again."
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title={slotTitle}
                className="pb-4 border-b border-line-base"
            />

            <FormCard
                title="Source of Treasury Funds"
                hint="Description of the source of funds. Token sale, VC raise, business revenue, foundation grant, etc."
            >
                <Textarea
                    interaction="lightbar"
                    aria-label="Source of Treasury Funds"
                    surface="filled"
                    size="md"
                    value={sourceOfFunds}
                    onChange={(e) =>
                        setState((prev) => ({
                            ...prev,
                            sourceOfFunds: e.target.value
                        }))
                    }
                    placeholder="e.g. Treasury funded by our August 2024 Series A raise of $25M led by [VC] with participation from [VCs]. Held primarily in USDC on Solana."
                    rows={4}
                    className="resize-y"
                />
            </FormCard>

            <FormCard
                title="Jurisdiction of Primary Operations"
                hint="Where you operate day-to-day for your business activity. May differ from your jurisdiction of incorporation."
            >
                <Textarea
                    interaction="lightbar"
                    aria-label="Jurisdiction of Primary Operations"
                    surface="filled"
                    size="md"
                    value={jurisdiction}
                    onChange={(e) =>
                        setState((prev) => ({
                            ...prev,
                            jurisdiction: e.target.value
                        }))
                    }
                    placeholder="e.g. Distributed team across the US (5), Singapore (2), Portugal (1). Decision-making centered with the founding team in New York and Singapore. Foundation is a Cayman entity but no operations occur there."
                    rows={4}
                    className="resize-y"
                />
            </FormCard>

            <FormPrimaryAction
                submittable={submittable}
                submitting={submitting}
                isSavedClean={lastSubmitted !== null && !isDirty}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
                onSave={() => void handleSave()}
                error={submitError}
            />
        </motion.div>
    );
}
